import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FilterRequest, SettingsService, CompanyRegularExpressionRequest, CompanyRegularExpressionResponsePaginatedList, RegularExpressionType} from '@src/app/shared/backend/clients';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-patterns-settings',
    templateUrl: './patterns-settings.component.html',
    styleUrls: ['./patterns-settings.component.scss'],
    host: {
        class: 'app-patterns-settings'
    },
    standalone: false
})
export class PatternsSettingsComponent implements OnInit {

  // local pagination
  paginatorFirst: number = 0;
  paginatorRows: number = 10;

  private readonly fetchPageSize: number = 200;

  private allPatterns: any[] = [];

  data: any[] = [];

  get totalRecords(): number {
    return this.allPatterns.length;
  }

  protected filter: FilterRequest = {
    pageSize: this.fetchPageSize,
    pageNumber: 1
  };

  page: CompanyRegularExpressionResponsePaginatedList = {};
  showCreatePatternModal = false;
  showViewPatternModal = false;
  selectedPatternId: number = 0;
  activeTab: number[] = [0]; // accordion first tab open (supports multiple)

  patternTypes = [
    { value: RegularExpressionType.Warranty },
    { value: RegularExpressionType.Retailer },
    { value: RegularExpressionType.Item }
  ];

  patternTypeOptions = this.patternTypes;
  patternTypeTotalRecords = this.patternTypeOptions.length;
  patternTypeRowsPerPageOption = 5;

  filterOperator = FilterOperator;

  stateOptions = [
    { label: "On", value: true },
    { label: "Off", value: false }
  ];

  public patternForm: FormGroup = this.fb.group({
    regularExpressionType: [[], [Validators.required]],
    pattern: [null, [Validators.required]]
  });

  public updatePatternForm: FormGroup = this.fb.group({
    regularExpressionType: [[], [Validators.required]],
    pattern: [null, [Validators.required]]
  });

  constructor(
    private fb: FormBuilder,
    public settingsService: SettingsService
  ) { }

  async ngOnInit() {
    this.reset();
    await this.get();
  }

  async get() {
    this.allPatterns = await this.fetchAllPatterns();
    this.normalizePaginatorFirst();
    this.updatePageSlice();
  }

  private async fetchAllPatterns(): Promise<any[]> {
    const results: any[] = [];

    let pageNumber = 1;

    while (true) {
      const request: FilterRequest = {
        ...this.filter,
        pageNumber: pageNumber,
        pageSize: this.fetchPageSize
      };

      const page = await firstValueFrom(this.settingsService.apiSettingsRegularExpressionFilterPost(request));

      const pageItems = page.results ?? [];

      const totalCount = page.totalCount ?? results.length;
      results.push(...pageItems);

      const receivedNoItems = pageItems.length === 0;
      const receivedAllItems = results.length >= totalCount;

      if (receivedNoItems || receivedAllItems) {
        this.page = page;
        break;
      }

      pageNumber += 1;
    }

    return results;
  }

  private updatePageSlice(): void {
    const startIndex = this.paginatorFirst;
    const endIndex = startIndex + this.paginatorRows;

    this.data = this.allPatterns.slice(startIndex, endIndex);
  }

  private normalizePaginatorFirst(): void {
    if (this.paginatorRows <= 0) {
      this.paginatorRows = 10;
    }

    if (this.totalRecords <= 0) {
      this.paginatorFirst = 0;
      return;
    }

    const lastPageIndex = Math.max(0, Math.ceil(this.totalRecords / this.paginatorRows) - 1);
    const maxFirstIndex = lastPageIndex * this.paginatorRows;

    if (this.paginatorFirst < 0) {
      this.paginatorFirst = 0;
      return;
    }

    if (this.paginatorFirst > maxFirstIndex) {
      this.paginatorFirst = maxFirstIndex;
    }
  }

  getPatternTypeTotalPages(): number {
    return Math.max(1, Math.ceil((this.patternTypeTotalRecords ?? 0) / (this.patternTypeRowsPerPageOption || 1)));
  }

  onPageChange(event: any) {
    this.paginatorFirst = event.first;
    this.paginatorRows = event.rows;

    this.updatePageSlice();
  }

  reset() {
    this.patternForm.reset({
      regularExpressionType: [],
      pattern: null
    });

    this.updatePatternForm.reset({
      regularExpressionType: [],
      pattern: null
    });
  }

  cancelCreatePattern() {
    this.showCreatePatternModal = false;
    this.reset();
  }

  displayCreatePatternModalbox() {
    this.showCreatePatternModal = true;
  }

  async onRemovePattern(event: MouseEvent, patternId: number) {
    event.preventDefault();
    await firstValueFrom(this.settingsService.apiSettingsRegularExpressionDeleteIdDelete(patternId));
    this.reset();
    await this.get();
  }

  async createPattern() {
    const data: CompanyRegularExpressionRequest = {
      pattern: this.patternForm.controls['pattern'].value,
      regularExpressionType: this.patternForm.controls['regularExpressionType'].value?.[0]
    };

    await firstValueFrom(this.settingsService.apiSettingsRegularExpressionCreatePost(data));
    this.showCreatePatternModal = false;
    this.reset();
    await this.get();
  }

  onViewPattern(event: MouseEvent, data: any) {
    event.preventDefault();
    this.showViewPatternModal = true;
    this.selectedPatternId = data.id;
    this.updatePatternForm.patchValue({
      pattern: data.pattern,
      regularExpressionType: data.regularExpressionType ? [data.regularExpressionType] : []
    });
  }

  async savePattern() {
    const data: CompanyRegularExpressionRequest = {
      pattern: this.updatePatternForm.controls['pattern'].value,
      regularExpressionType: this.updatePatternForm.controls['regularExpressionType'].value?.[0]
    };

    await firstValueFrom(this.settingsService.apiSettingsRegularExpressionUpdateIdPut(this.selectedPatternId, data));
    this.showViewPatternModal = false;
    this.reset();
    this.selectedPatternId = 0;
    await this.get();
  }

  cancelViewPattern() {
    this.showViewPatternModal = false;
    this.reset();
    this.selectedPatternId = 0;
  }

  async onStateChange(event: any, pattern: any) {
    const stateNewValue = event.checked;

    if (!stateNewValue) {
      await firstValueFrom(this.settingsService.apiSettingsRegularExpressionIdDeactivatePut(pattern.id));
    } else if (stateNewValue) {
      await firstValueFrom(this.settingsService.apiSettingsRegularExpressionIdActivatePut(pattern.id));
    }

    await this.get();
  }
}
